import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { insuranceQuotationsStore } from './QuotationsListContainer';
import ViewColumn from '@mui/icons-material/ViewColumn';
import ViewList from '@mui/icons-material/ViewColumn';
import QuotationGridComponent from './grid/QuotationGridComponent';
import QuotationListComponent from './list/QuotationListComponent';
import QuotationListLoadingComponent from './list/QuotationListLoadingComponent';
import QuotationGridLoadingComponent from './grid/QuotationGridLoadingComponent';
import { useTranslation } from 'react-i18next';
import useViewModes from 'features/common/hooks/useViewModes';
import QuotationContainer from './QuotationContainer';
import { Grid } from '@mui/material';
import { useReactiveVar } from '@apollo/client';
import { headerDataVar, offerInNegotiationStateVar } from '../variables';
import { useParams } from 'hooks';
import { quotationsInitializedVar, quotationsStatusVar } from 'features/insuranceDocument/variables';
import { QuotationsStatus, cascoLoadingFakeCardsNo } from 'constants/defaultValues';
import EmptyQuotationsListComponent from './EmptyQuotationsListComponent';

const QuotationsContainer = () => {
  const { t } = useTranslation();
  const headerData = useReactiveVar(headerDataVar);
  const quotationStatus = useReactiveVar(quotationsStatusVar);
  const quotationsInitialized = useReactiveVar(quotationsInitializedVar);

  useParams('OfferInNegotiationDocumentStateCode', offerInNegotiationStateVar);

  const {
    store: { values, loading: listLoading, initialized }
  } = useListBrowserUtils(insuranceQuotationsStore);

  const viewModes = useMemo(
    () => [
      {
        icon: ViewColumn,
        caption: t('Tooltips.Grid'),
        name: 'Grid',
        components: { default: QuotationGridComponent, loading: QuotationGridLoadingComponent }
      },
      {
        icon: ViewList,
        caption: t('Tooltips.List'),
        name: 'List',
        components: { default: QuotationListComponent, loading: QuotationListLoadingComponent }
      }
    ],
    [t]
  );

  const { ViewModesComponent, Components } = useViewModes(viewModes, 'CASCO_Quotations_View_Mode');

  const loading = useMemo(
    () => !initialized || listLoading || !headerData || quotationStatus === QuotationsStatus.PROCESSING || quotationsInitialized,
    [initialized, listLoading, headerData, quotationStatus, quotationsInitialized]
  );

  return (
    <>
      <ViewModesComponent />
      <Grid container spacing={2}>
        {loading && Array.from({ length: cascoLoadingFakeCardsNo }).map((_, index) => <Components.loading key={index} />)}
        {!loading && values.length === 0 && <EmptyQuotationsListComponent />}
        {!loading &&
          values.length >= 0 &&
          values.map((item, index) => (
            <QuotationContainer
              key={item.quotationId}
              index={index}
              data={item}
              selected={item.quotationId === headerData?.quotationId}
              ItemComponent={Components.default}
            />
          ))}
      </Grid>
    </>
  );
};

QuotationsContainer.propTypes = {
  list: PropTypes.array,
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  pager: PropTypes.object,
  quotationsStatus: PropTypes.string,
  addQuotationsEnabled: PropTypes.bool,
  allowQuotationsLockUpdate: PropTypes.bool,
  computing: PropTypes.bool,
  currentStateCode: PropTypes.string,
  onEditCustomQuotation: PropTypes.func,
  onQuotationDataChanged: PropTypes.func,
  headerData: PropTypes.array,
  loadingHeaderData: PropTypes.bool,
  setHasHistory: PropTypes.func
};

export default QuotationsContainer;
